import {
    FileTextOutlined,
    DownloadOutlined,
    EyeOutlined,
    CalendarOutlined,
    ReloadOutlined,
    PrinterOutlined,
    FileSearchOutlined,
    BankOutlined
} from '@ant-design/icons';
import {
    ConfigProvider,
    DatePicker,
    Select,
    Card,
    Table,
    Tag,
    Space,
    Button,
    Row,
    Col,
    Statistic,
    Collapse,
    Descriptions,
    Divider,
    Modal
} from 'antd';
import frFR from "antd/locale/fr_FR";
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Footer from '../../../components/Footer';
import NavBar from '../../../components/NavBar';
import SideBar from '../../../components/SideBar';
import axiosInstance from '../../../variables/api/axiosInstance';
import { LOGGED_USER } from '../../../variables/constants';
import { frenchMonth } from '../../../variables/functions';
import { displayNumbers } from '../../../variables/numbersFunctions';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const { Panel } = Collapse;

const RetrocessionDocuments = () => {
    const [retrocessions, setRetrocessions] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPeriode, setSelectedPeriode] = useState(null);
    const [selectedCategorie, setSelectedCategorie] = useState(null);
    const [groupedData, setGroupedData] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);

    const isPoolUser = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";

    useEffect(() => {
        chargerCategories();
    }, []);

    useEffect(() => {
        if (selectedPeriode || selectedCategorie) {
            chargerRetrocessions();
        }
    }, [selectedPeriode, selectedCategorie]);

    const chargerCategories = async () => {
        try {
            const response = await axiosInstance.get('/api/categorie');
            const excludedCodes = ["A", "B", "T"];
            const filtered = response.data.filter(
                (c) => !c.codeCategorie.startsWith("V") && !excludedCodes.includes(c.codeCategorie)
            );

            const hasTPVCategories = response.data.some((c) => excludedCodes.includes(c.codeCategorie));
            if (hasTPVCategories) {
                filtered.push({ codeCategorie: "TPV", nom: "TPV (Synthétique)", synthetic: true });
                filtered.push({ codeCategorie: "MOTO", nom: "MOTO", synthetic: true });
            }

            setCategories(filtered);
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du chargement des catégories");
        }
    };

    const chargerRetrocessions = async () => {
        setLoading(true);
        try {
            const params = {};
            if (selectedPeriode) params.mois = selectedPeriode;
            if (selectedCategorie) params.categorie = selectedCategorie;

            const response = await axiosInstance.get('/api/retrocession', { params });
            setRetrocessions(response.data);
            grouperDonnees(response.data);
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du chargement des rétrocessions");
        } finally {
            setLoading(false);
        }
    };

    const grouperDonnees = (data) => {
        const grouped = {};

        data.forEach(retro => {
            const moisKey = dayjs(retro.moisRetrocession).format('YYYY-MM');
            const categorieKey = retro.categorie.codeCategorie;
            const key = `${moisKey}-${categorieKey}`;

            if (!grouped[key]) {
                grouped[key] = {
                    mois: moisKey,
                    categorie: retro.categorie,
                    compagnies: {},
                    totalEmissions: 0,
                    totalAnnulations: 0,
                    totalSinistres: 0,
                    totalRecours: 0,
                    totalPenalites: 0
                };
            }

            const compagnieCode = retro.compagnie.code;
            if (!grouped[key].compagnies[compagnieCode]) {
                grouped[key].compagnies[compagnieCode] = {
                    compagnie: retro.compagnie,
                    retrocessions: []
                };
            }

            grouped[key].compagnies[compagnieCode].retrocessions.push(retro);
            grouped[key].totalEmissions += retro.totalEmissions || 0;
            grouped[key].totalAnnulations += retro.totalAnnulations || 0;
            grouped[key].totalSinistres += retro.totalSinistres || 0;
            grouped[key].totalRecours += retro.totalRecours || 0;
            grouped[key].totalPenalites += retro.penalite || 0;
        });

        setGroupedData(Object.values(grouped));
    };

    // Génération du PDF - Compte de Cession
    const genererCompteCession = (retro) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        // En-tête
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('POOL TPVM-VT GIE', pageWidth / 2, 20, { align: 'center' });

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text('01 BP 4866 - Tél. : 22-20-55-77/22-20-55-78', pageWidth / 2, 27, { align: 'center' });

        // Cadre titre
        doc.setFillColor(230, 230, 230);
        doc.rect(15, 40, 60, 25, 'F');
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('COMPTE DE CESSION', 17, 48);
        doc.setFontSize(10);
        doc.text(`Catégorie : ${retro.categorie.codeCategorie}`, 17, 56);
        doc.text(`Période : ${frenchMonth(dayjs(retro.moisRetrocession).format('MM'))} ${dayjs(retro.moisRetrocession).format('YYYY')}`, 17, 62);

        // Informations compagnie
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(retro.compagnie.raisonSociale, pageWidth - 15, 48, { align: 'right' });
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(retro.compagnie.adresse || '', pageWidth - 15, 54, { align: 'right' });
        doc.text(`Tél : ${retro.compagnie.telephone || ''}`, pageWidth - 15, 59, { align: 'right' });

        // Table des montants
        const tableData = [
            ['MONTANT TOTAL DES SOUSCRIPTIONS', '', displayNumbers(retro.totalEmissions || 0)],
            ['INDEMNITES DE GESTION 15%', displayNumbers((retro.totalEmissions || 0) * 0.15), ''],
            ['SINISTRES PAYES', displayNumbers(retro.totalSinistres || 0), ''],
            ['SOLDE EN FAVEUR DU POOL', displayNumbers((retro.totalEmissions || 0) - (retro.totalSinistres || 0) - ((retro.totalEmissions || 0) * 0.15)), ''],
            ['BALANCE', displayNumbers((retro.totalEmissions || 0) - (retro.totalSinistres || 0) - ((retro.totalEmissions || 0) * 0.15)), displayNumbers((retro.totalEmissions || 0) - (retro.totalSinistres || 0) - ((retro.totalEmissions || 0) * 0.15))]
        ];

        doc.autoTable({
            startY: 80,
            head: [['Postes', 'Débits', 'Crédits']],
            body: tableData,
            theme: 'grid',
            styles: { fontSize: 9, cellPadding: 3 },
            headStyles: { fillColor: [200, 200, 200], textColor: 0, fontStyle: 'bold' },
            columnStyles: {
                0: { cellWidth: 100 },
                1: { cellWidth: 40, halign: 'right' },
                2: { cellWidth: 40, halign: 'right' }
            }
        });

        // Signature
        const finalY = doc.lastAutoTable.finalY + 30;
        doc.setFontSize(10);
        doc.text('Directeur du Pool', pageWidth - 15, finalY, { align: 'right' });
        doc.setFont('helvetica', 'bold');
        doc.text('Koffi G. WARBUTIN', pageWidth - 15, finalY + 15, { align: 'right' });

        // Note de bas de page
        doc.setFontSize(7);
        doc.setFont('helvetica', 'italic');
        doc.text('Ce document ne peut être ni repris, ni annulé. Toute erreur constatée doit être validée par nos soins pour être reportée sur le document suivant.', pageWidth / 2, pageHeight - 15, { align: 'center', maxWidth: pageWidth - 40 });
        doc.text('208, Boulevard du 13 janvier Lomé - RC N° - AGREMENT : 2009 C 0771 -', pageWidth / 2, pageHeight - 10, { align: 'center' });

        return doc;
    };

    // Génération du PDF - Compte de Rétrocession
    const genererCompteRetrocession = (retro) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        // En-tête
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('POOL TPVM-VT GIE', pageWidth / 2, 20, { align: 'center' });

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text('01 BP 4866 - Tél. : 22-20-55-77/22-20-55-78', pageWidth / 2, 27, { align: 'center' });

        // Cadre titre
        doc.setFillColor(230, 230, 230);
        doc.rect(15, 40, 70, 25, 'F');
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('COMPTE DE RETROCESSION', 17, 48);
        doc.setFontSize(10);
        doc.text(`Catégorie : ${retro.categorie.codeCategorie}`, 17, 56);
        doc.text(`Période : ${frenchMonth(dayjs(retro.moisRetrocession).format('MM'))} ${dayjs(retro.moisRetrocession).format('YYYY')}`, 17, 62);

        // Informations compagnie
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(retro.compagnie.raisonSociale, pageWidth - 15, 48, { align: 'right' });
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(retro.compagnie.adresse || '', pageWidth - 15, 54, { align: 'right' });
        doc.text(`Tél : ${retro.compagnie.telephone || ''}`, pageWidth - 15, 59, { align: 'right' });

        // Données parsées depuis JSON
        let emissionsData = [];
        try {
            emissionsData = JSON.parse(retro.emissionsData || '[]');
        } catch (e) {
            console.error('Erreur parsing emissions:', e);
        }

        const montantEmission = emissionsData.reduce((sum, e) => sum + (e.montantRetrocede || 0), 0);
        const indemnitesGestion = montantEmission * 0.15;
        const fraisAdmin = montantEmission * 0.06;
        const preventionRoutiere = montantEmission * 0.01;
        const reserveGarantie = montantEmission * 0.05;

        // Table des montants
        const tableData = [
            ['MONTANT EMISSION NETTE RETRO', '', displayNumbers(montantEmission)],
            ['SINISTRES REGLES RETRO', displayNumbers(retro.totalSinistres || 0), ''],
            ['INDEMNITE GESTION SUR CA RETRO 15%', displayNumbers(indemnitesGestion), ''],
            ['FRAIS ADMINISTRATIFS DU POOL 6%', displayNumbers(fraisAdmin), ''],
            ['PREVENTION ROUTIERE TOGOLAISE 1%', displayNumbers(preventionRoutiere), ''],
            ['RESERVE DE GARANTIE 5%', displayNumbers(reserveGarantie), ''],
            ['DIFF. SOLDE CESSION FAVEUR POOL', displayNumbers(retro.soldeGlobal || 0), ''],
            ['SOLDE EN FAVEUR DE LA COMPAGNIE', displayNumbers(montantEmission - (retro.totalSinistres || 0) - indemnitesGestion - fraisAdmin - preventionRoutiere - reserveGarantie), ''],
            ['BALANCE', displayNumbers(montantEmission), displayNumbers(montantEmission)]
        ];

        doc.autoTable({
            startY: 80,
            head: [['Postes', 'Débits', 'Crédits']],
            body: tableData,
            theme: 'grid',
            styles: { fontSize: 9, cellPadding: 3 },
            headStyles: { fillColor: [200, 200, 200], textColor: 0, fontStyle: 'bold' },
            columnStyles: {
                0: { cellWidth: 100 },
                1: { cellWidth: 40, halign: 'right' },
                2: { cellWidth: 40, halign: 'right' }
            }
        });

        // Signature
        const finalY = doc.lastAutoTable.finalY + 30;
        doc.setFontSize(10);
        doc.text('Directeur du Pool', pageWidth - 15, finalY, { align: 'right' });
        doc.setFont('helvetica', 'bold');
        doc.text('Koffi G. WARBUTIN', pageWidth - 15, finalY + 15, { align: 'right' });

        // Note de bas de page
        doc.setFontSize(7);
        doc.setFont('helvetica', 'italic');
        doc.text('Ce document ne peut être ni repris, ni annulé. Toute erreur constatée doit être validée par nos soins pour être reportée sur le document suivant.', pageWidth / 2, pageHeight - 15, { align: 'center', maxWidth: pageWidth - 40 });
        doc.text('208, Boulevard du 13 janvier Lomé - RC N° - AGREMENT : 2009 C 0771 -', pageWidth / 2, pageHeight - 10, { align: 'center' });

        return doc;
    };

    // Génération du récapitulatif global
    const genererRecapitulatifGlobal = (groupe) => {
        const doc = new jsPDF('landscape');
        const pageWidth = doc.internal.pageSize.getWidth();

        // En-tête
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('POOL TPVM-VT GIE', pageWidth / 2, 15, { align: 'center' });

        doc.setFontSize(14);
        doc.text(`RETRO TOUS EXERCICES ${groupe.categorie.codeCategorie} ${frenchMonth(groupe.mois.split('-')[1])} ${groupe.mois.split('-')[0]}`, pageWidth / 2, 25, { align: 'center' });

        doc.setFontSize(10);
        doc.text(`Edité le ${dayjs().format('DD MMMM YYYY')} par ${LOGGED_USER.nom} ${LOGGED_USER.prenom}`, pageWidth / 2, 32, { align: 'center' });

        // Données par compagnie
        const compagniesData = Object.values(groupe.compagnies).map(comp => {
            const totalEmissions = comp.retrocessions.reduce((sum, r) => sum + (r.totalEmissions || 0), 0);
            const totalSinistres = comp.retrocessions.reduce((sum, r) => sum + (r.totalSinistres || 0), 0);
            const solde = totalEmissions - totalSinistres;

            return [
                comp.compagnie.raisonSociale,
                displayNumbers(totalEmissions),
                displayNumbers(totalSinistres),
                displayNumbers(solde)
            ];
        });

        doc.autoTable({
            startY: 45,
            head: [['COMPAGNIE', 'RETRO', 'SINISTRES', 'SOLDE']],
            body: compagniesData,
            foot: [[
                'TOTAL',
                displayNumbers(groupe.totalEmissions),
                displayNumbers(groupe.totalSinistres),
                displayNumbers(groupe.totalEmissions - groupe.totalSinistres)
            ]],
            theme: 'grid',
            styles: { fontSize: 10, cellPadding: 4 },
            headStyles: { fillColor: [240, 160, 18], textColor: 255, fontStyle: 'bold' },
            footStyles: { fillColor: [200, 200, 200], fontStyle: 'bold' }
        });

        return doc;
    };

    const telechargerDocument = (type, data) => {
        let doc;
        let filename;

        switch (type) {
            case 'cession':
                doc = genererCompteCession(data);
                filename = `Compte_Cession_${data.compagnie.raisonSociale}_${data.categorie.codeCategorie}_${dayjs(data.moisRetrocession).format('MM-YYYY')}.pdf`;
                break;
            case 'retrocession':
                doc = genererCompteRetrocession(data);
                filename = `Compte_Retrocession_${data.compagnie.raisonSociale}_${data.categorie.codeCategorie}_${dayjs(data.moisRetrocession).format('MM-YYYY')}.pdf`;
                break;
            case 'recapitulatif':
                doc = genererRecapitulatifGlobal(data);
                filename = `Recapitulatif_${data.categorie.codeCategorie}_${data.mois}.pdf`;
                break;
            default:
                return;
        }

        doc.save(filename);
        toast.success('Document téléchargé avec succès');
    };

    const previsualiserDocument = (type, data) => {
        let doc;

        switch (type) {
            case 'cession':
                doc = genererCompteCession(data);
                break;
            case 'retrocession':
                doc = genererCompteRetrocession(data);
                break;
            case 'recapitulatif':
                doc = genererRecapitulatifGlobal(data);
                break;
            default:
                return;
        }

        const pdfBlob = doc.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);

        setSelectedDocument({ url: pdfUrl, type, data });
        setModalVisible(true);
    };

    const colonnesCompagnies = [
        {
            title: 'Compagnie',
            key: 'compagnie',
            render: (_, record) => (
                <span style={{ fontWeight: 600 }}>{record.compagnie.raisonSociale}</span>
            )
        },
        {
            title: 'Total Émissions',
            key: 'totalEmissions',
            render: (_, record) => {
                const total = record.retrocessions.reduce((sum, r) => sum + (r.totalEmissions || 0), 0);
                return (
                    <span style={{ color: '#52c41a', fontWeight: 600 }}>
                        {displayNumbers(total)} F CFA
                    </span>
                );
            },
            align: 'right'
        },
        {
            title: 'Total Sinistres',
            key: 'totalSinistres',
            render: (_, record) => {
                const total = record.retrocessions.reduce((sum, r) => sum + (r.totalSinistres || 0), 0);
                return (
                    <span style={{ color: '#ff4d4f', fontWeight: 600 }}>
                        {displayNumbers(total)} F CFA
                    </span>
                );
            },
            align: 'right'
        },
        {
            title: 'Pénalités',
            key: 'penalites',
            render: (_, record) => {
                const total = record.retrocessions.reduce((sum, r) => sum + (r.penalite || 0), 0);
                return total > 0 ? (
                    <span style={{ color: '#faad14', fontWeight: 600 }}>
                        {displayNumbers(total)} F CFA
                    </span>
                ) : (
                    <span style={{ color: '#52c41a' }}>-</span>
                );
            },
            align: 'right'
        },
        {
            title: 'Documents',
            key: 'actions',
            render: (_, record) => (
                <Space direction="vertical" size="small">
                    <Space>
                        <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => previsualiserDocument('cession', record.retrocessions[0])}
                        >
                            Compte de Cession
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={() => telechargerDocument('cession', record.retrocessions[0])}
                        />
                    </Space>
                    <Space>
                        <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => previsualiserDocument('retrocession', record.retrocessions[0])}
                        >
                            Compte de Rétrocession
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={() => telechargerDocument('retrocession', record.retrocessions[0])}
                        />
                    </Space>
                </Space>
            ),
            width: 300
        }
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <style>{`
        .retro-docs-header {
          background: linear-gradient(135deg, #722ed1 0%, #531dab 100%);
          border-radius: 16px;
          padding: 32px;
          margin-bottom: 24px;
          box-shadow: 0 4px 20px rgba(114, 46, 209, 0.25);
          color: white;
        }

        .retro-docs-header h2 {
          color: white;
          font-size: 28px;
          font-weight: 700;
          margin: 0 0 8px 0;
        }

        .filter-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-bottom: 24px;
        }

        .stats-card {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-bottom: 24px;
        }

        .collapse-header {
          background: linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%);
          border-radius: 8px;
          padding: 12px 16px;
          font-weight: 600;
          font-size: 15px;
        }
      `}</style>

            <div className="layout-container">
                <SideBar />

                <div className="layout-page">
                    <NavBar />

                    <div className="content-wrapper" style={{ background: '#f5f5f5' }}>
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête */}
                            <div className="retro-docs-header">
                                <h2>
                                    <FileTextOutlined /> Documents de Rétrocession
                                </h2>
                                <p>Consultation et génération des comptes de cession et rétrocession</p>
                            </div>

                            {/* Filtres */}
                            <div className="filter-card">
                                <Row gutter={16}>
                                    <Col xs={24} sm={8}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                                            <CalendarOutlined /> Période
                                        </label>
                                        <DatePicker
                                            placeholder="Sélectionner le mois"
                                            onChange={(_, dateString) => setSelectedPeriode(dateString)}
                                            picker="month"
                                            style={{ width: '100%' }}
                                        />
                                    </Col>

                                    <Col xs={24} sm={8}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                                            <FileTextOutlined /> Catégorie
                                        </label>
                                        <Select
                                            placeholder="Toutes les catégories"
                                            style={{ width: '100%' }}
                                            onChange={setSelectedCategorie}
                                            allowClear
                                            options={categories.map(c => ({
                                                value: c.codeCategorie,
                                                label: c.nom
                                            }))}
                                        />
                                    </Col>

                                    <Col xs={24} sm={8}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                                            &nbsp;
                                        </label>
                                        <Button
                                            type="primary"
                                            icon={<ReloadOutlined />}
                                            onClick={chargerRetrocessions}
                                            loading={loading}
                                            style={{ width: '100%' }}
                                        >
                                            Actualiser
                                        </Button>
                                    </Col>
                                </Row>
                            </div>

                            {/* Statistiques */}
                            {groupedData.length > 0 && (
                                <div className="stats-card">
                                    <Row gutter={16}>
                                        <Col xs={12} sm={6}>
                                            <Statistic
                                                title="Périodes"
                                                value={new Set(groupedData.map(g => g.mois)).size}
                                                prefix={<CalendarOutlined />}
                                                valueStyle={{ color: '#722ed1' }}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <Statistic
                                                title="Catégories"
                                                value={new Set(groupedData.map(g => g.categorie.codeCategorie)).size}
                                                prefix={<FileTextOutlined />}
                                                valueStyle={{ color: '#1890ff' }}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <Statistic
                                                title="Compagnies"
                                                value={groupedData.reduce((sum, g) => sum + Object.keys(g.compagnies).length, 0)}
                                                prefix={<BankOutlined />}
                                                valueStyle={{ color: '#52c41a' }}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <Statistic
                                                title="Documents"
                                                value={groupedData.reduce((sum, g) => sum + Object.keys(g.compagnies).length * 2, 0)}
                                                prefix={<FileSearchOutlined />}
                                                valueStyle={{ color: '#faad14' }}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            )}

                            {/* Données groupées */}
                            {groupedData.length > 0 ? (
                                <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
                                    <ConfigProvider locale={frFR}>
                                        <Collapse accordion>
                                            {groupedData.map((groupe, index) => (
                                                <Panel
                                                    key={index}
                                                    header={
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <span>
                                                                <Tag color="purple">{groupe.categorie.codeCategorie}</Tag>
                                                                <strong>
                                                                    {frenchMonth(groupe.mois.split('-')[1])} {groupe.mois.split('-')[0]}
                                                                </strong>
                                                            </span>
                                                            <Space>
                                                                <Tag color="green">
                                                                    Émissions: {displayNumbers(groupe.totalEmissions)} F
                                                                </Tag>
                                                                <Tag color="red">
                                                                    Sinistres: {displayNumbers(groupe.totalSinistres)} F
                                                                </Tag>
                                                                <Button
                                                                    type="primary"
                                                                    size="small"
                                                                    icon={<FileTextOutlined />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        telechargerDocument('recapitulatif', groupe);
                                                                    }}
                                                                >
                                                                    Récapitulatif Global
                                                                </Button>
                                                            </Space>
                                                        </div>
                                                    }
                                                >
                                                    <Table
                                                        columns={colonnesCompagnies}
                                                        dataSource={Object.values(groupe.compagnies)}
                                                        rowKey={(record) => record.compagnie.code}
                                                        pagination={false}
                                                        size="small"
                                                    />
                                                </Panel>
                                            ))}
                                        </Collapse>
                                    </ConfigProvider>
                                </Card>
                            ) : (
                                <Card style={{ textAlign: 'center', padding: '40px' }}>
                                    <FileSearchOutlined style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }} />
                                    <h3>Aucune rétrocession trouvée</h3>
                                    <p>Sélectionnez une période et/ou une catégorie pour afficher les documents</p>
                                </Card>
                            )}

                        </div>

                        <Footer />
                    </div>
                </div>
            </div>

            {/* Modal de prévisualisation */}
            <Modal
                title="Prévisualisation du document"
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    if (selectedDocument?.url) {
                        URL.revokeObjectURL(selectedDocument.url);
                    }
                    setSelectedDocument(null);
                }}
                width="90%"
                style={{ top: 20 }}
                footer={[
                    <Button key="close" onClick={() => setModalVisible(false)}>
                        Fermer
                    </Button>,
                    <Button
                        key="download"
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={() => {
                            if (selectedDocument) {
                                telechargerDocument(selectedDocument.type, selectedDocument.data);
                            }
                        }}
                    >
                        Télécharger
                    </Button>
                ]}
            >
                {selectedDocument?.url && (
                    <iframe
                        src={selectedDocument.url}
                        style={{ width: '100%', height: '70vh', border: 'none' }}
                        title="Document PDF"
                    />
                )}
            </Modal>

            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default RetrocessionDocuments;